import { MarkSettledDialog } from "./_components/MarkSettledDialog";
import { useMemo, useState } from "react";
import { useDisputeParams } from "./params";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import toast from "react-hot-toast";
import { Search } from "lucide-react";

import { usePageTitle } from "@/hooks/usePageTitle";
import { withBranding } from "@/utils/helper";
import { Badge } from "@/components/ui/badge";
import { statusToVariant } from "@/components/ui/badge/statusToVariant";
import { Button } from "@/components/ui/button";
import { PermissionButton } from "@/components/ui/permission-button";
import { PLATFORM_SECTION_ENUM } from "@/utils/enums";
import { ViewAction } from "@/components/table/actions";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Table, type ColumnDef } from "@/components/table";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/utils/cn";
import { ROUTES } from "@/constants/routes";
import {
  useListCompanyDisputesQuery,
  useListSettleableDisputesQuery,
  useSettleDisputesWpsMutation,
  type DisputeStatus,
  type IDispute,
  type ISettleableDispute,
} from "@/services/(company)/payrollDisputesApi";
import { Trans, useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

const STATUS_ALL = "all" as const;

const PAYMENT_METHOD_KEY: Record<string, string> = {
  WPS: "common:payment_methods.wps",
  BANK_TRANSFER: "common:payment_methods.bank_transfer",
  CASH: "common:payment_methods.cash",
  CHEQUE: "common:payment_methods.cheque",
  UNASSIGNED: "common:payment_methods.unassigned",
};
const buildStatusOptions = (
  translate: TFunction,
): { value: typeof STATUS_ALL | DisputeStatus; label: string }[] => [
  { value: STATUS_ALL, label: translate("company:payrolls.all_statuses") },
  { value: "PENDING", label: translate("common:status.pending") },
  { value: "RESOLVED", label: translate("company:payrolls.resolved") },
  { value: "REJECTED", label: translate("common:status.rejected") },
];

type SubTab = "all" | "settleable";

const getInitials = (name: string) =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("") || "?";

const formatAmount = (n: number) =>
  n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export const CompanyDisputes = () => {
  const { t: translate } = useTranslation(["company", "common"]);
  usePageTitle(withBranding(translate("company:payrolls.payroll_disputes")));
  const [subTab, setSubTab] = useState<SubTab>("all");

  return (
    <div className="rounded-xs border border-border bg-white flex flex-col gap-4 p-4">
      <div className="flex items-center gap-6 border-b border-border -mx-4 px-4">
        {[
          { value: "all", label: translate("company:payrolls.all_disputes") },
          {
            value: "settleable",
            label: translate("company:payrolls.pending_settlement"),
          },
        ].map((t) => {
          const isActive = subTab === t.value;
          return (
            <button
              key={t.value}
              type="button"
              onClick={() => setSubTab(t.value as SubTab)}
              className={cn(
                "relative -mb-px py-2 text-sm font-medium transition-colors",
                isActive
                  ? "text-gray-900 border-b-2 border-primary"
                  : "text-gray-400 hover:text-gray-600",
              )}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {subTab === "all" ? <AllDisputesList /> : <SettleableDisputesList />}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// All Disputes — original list
// ─────────────────────────────────────────────────────────────────────────────
const AllDisputesList = () => {
  const { t: translate } = useTranslation(["company", "common"]);
  const navigate = useNavigate();
  // Search, filter and page all live in the URL — see useTableParams. Only
  // this sub-tab has a table, so no prefix is needed.
  const tp = useDisputeParams();
  const status = tp.filters.status;

  const { data, isFetching } = useListCompanyDisputesQuery({
    page: tp.page,
    limit: 20,
    ...(status !== STATUS_ALL ? { status: status as DisputeStatus } : {}),
    ...(tp.committedSearch ? { search: tp.committedSearch } : {}),
  });

  const disputes = useMemo(() => data?.data ?? [], [data]);

  const columns: ColumnDef<IDispute>[] = [
    {
      key: "employee",
      label: translate("common:labels.employee"),
      className: "w-[22%]",
      render: (r) => (
        <div className="flex items-center gap-2">
          <Avatar className="size-7">
            {r.raisedByUser?.profileImage && (
              <AvatarImage
                src={r.raisedByUser.profileImage}
                alt={r.raisedByUser.fullName}
              />
            )}
            <AvatarFallback className="bg-primary/10 text-primary text-[10px] font-bold">
              {getInitials(r.raisedByUser?.fullName ?? "?")}
            </AvatarFallback>
          </Avatar>
          <span className="text-sm font-medium text-gray-900 truncate">
            {r.raisedByUser?.fullName ?? "—"}
          </span>
        </div>
      ),
    },
    {
      key: "period",
      label: translate("company:payrolls.period"),
      className: "w-[12%]",
      render: (r) => (
        <span className="text-sm text-gray-700">
          {r.payrollRun?.periodStart
            ? format(new Date(r.payrollRun.periodStart), "MMM yyyy")
            : "—"}
        </span>
      ),
    },
    {
      key: "category",
      label: translate("company:payrolls.category"),
      className: "w-[14%]",
      render: (r) => (
        <span className="text-sm text-gray-700 capitalize">
          {translate(
            `company:payrolls.dispute_category_${r.category.toLowerCase()}`,
          )}
        </span>
      ),
    },
    {
      key: "lines",
      label: translate("company:payrolls.lines"),
      className: "w-[8%]",
      render: (r) => (
        <span className="text-sm text-gray-700">{r.lines.length}</span>
      ),
    },
    {
      key: "diff",
      label: translate("company:payrolls.net_diff"),
      className: "w-[12%]",
      render: (r) => (
        <span
          className={cn(
            "text-sm font-semibold tabular-nums",
            r.totalProposedDiff > 0
              ? "text-success"
              : r.totalProposedDiff < 0
                ? "text-danger"
                : "text-foreground",
          )}
        >
          {r.totalProposedDiff > 0 ? "+" : ""}
          {r.totalProposedDiff.toFixed(2)}
        </span>
      ),
    },
    {
      key: "status",
      label: translate("company:payrolls.dispute_status"),
      className: "w-[12%]",
      render: (r) => (
        <Badge variant={statusToVariant(r.status)}>
          {r.status === "UNDER_REVIEW"
            ? translate("company:payrolls.under_review")
            : translate(`common:status.${r.status.toLowerCase()}`)}
        </Badge>
      ),
    },
    {
      key: "paymentStatus",
      label: translate("company:payrolls.payment_status"),
      className: "w-[12%]",
      render: (r) => {
        if (r.wpsSettledAt)
          return (
            <Badge variant="success">
              {translate("company:payrolls.settled")}
            </Badge>
          );
        if (r.status === "RESOLVED")
          return (
            <Badge variant="warning">
              {translate("company:payrolls.pending_payment")}
            </Badge>
          );
        return <span className="text-muted-foreground text-[11px]">—</span>;
      },
    },
    {
      key: "createdAt",
      label: translate("company:payrolls.raised"),
      className: "w-[10%]",
      render: (r) => (
        <span className="text-xs text-gray-500">
          {format(new Date(r.createdAt), "dd MMM")}
        </span>
      ),
    },
    {
      key: "action",
      label: translate("company:payrolls.actions"),
      className: "w-[10%]",
      render: (r) => (
        <ViewAction
          tooltip={translate("company:payrolls.view")}
          onClick={() =>
            navigate(ROUTES.HOME.COMPANY.PAYROLL.DISPUTE_DETAIL(r.id))
          }
        />
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-2 border border-input rounded-xs h-9 px-3 w-80 bg-white">
          <Search size={14} className="text-gray-400" />
          <input
            type="text"
            value={tp.search}
            onChange={(e) => tp.setSearch(e.target.value)}
            placeholder={translate("company:payrolls.search_employee_name")}
            className="flex-1 bg-transparent text-xs placeholder:text-gray-400 focus:outline-none"
          />
        </div>
        <Select value={status} onValueChange={(v) => tp.setFilter("status", v)}>
          <SelectTrigger className="h-9 w-44 bg-white text-xs font-medium">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {buildStatusOptions(translate).map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-xs border border-border bg-white overflow-hidden">
        <Table<IDispute>
          columns={columns}
          data={disputes}
          loading={isFetching}
          emptyTitle={translate("company:payrolls.no_disputes")}
          emptyMessage={translate(
            "company:payrolls.employee_raised_disputes_will_appear_here",
          )}
        />
      </div>

      {(data?.meta?.totalPages ?? 1) > 1 && (
        <div className="flex justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={tp.page <= 1}
            onClick={() => tp.setPage(tp.page - 1)}
          >
            {translate("common:buttons.previous")}
          </Button>
          <span className="text-xs text-gray-600 self-center">
            {translate("company:payrolls.page_x_of_y", {
              page: tp.page,
              total: data?.meta?.totalPages ?? 1,
            })}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={tp.page >= (data?.meta?.totalPages ?? 1)}
            onClick={() => tp.setPage(tp.page + 1)}
          >
            {translate("common:buttons.next")}
          </Button>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Settleable — resolved + accepted, multi-select + generate WPS
// ─────────────────────────────────────────────────────────────────────────────
const SettleableDisputesList = () => {
  const { t: translate } = useTranslation(["company", "common"]);
  const navigate = useNavigate();
  const { data, isFetching, refetch } = useListSettleableDisputesQuery();
  const [settle, { isLoading: isSettling }] = useSettleDisputesWpsMutation();

  const [selected, setSelected] = useState<Set<number>>(new Set());

  const items = useMemo(() => data?.data ?? [], [data]);
  const allCount = items.length;
  const selectedCount = selected.size;

  const totalSelectedAmount = useMemo(
    () =>
      items
        .filter((i) => selected.has(i.id))
        .reduce((sum, i) => sum + i.adjustmentAmount, 0),
    [items, selected],
  );

  const hasWpsSelected = useMemo(
    () => items.some((i) => selected.has(i.id) && i.paymentMethod === "WPS"),
    [items, selected],
  );
  // Something a WPS file can pay: a WPS dispute with a positive adjustment.
  // A 0.00 or recovery adjustment is skipped by the server, so the button
  // must not offer it (B44).
  const hasPayableSelected = useMemo(
    () => items.some((i) => selected.has(i.id) && i.eligibleForWps),
    [items, selected],
  );

  const toggle = (id: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAll = () => {
    if (selected.size === allCount) {
      setSelected(new Set());
    } else {
      setSelected(new Set(items.map((i) => i.id)));
    }
  };

  // Settling by hand asks how, and records it (B47).
  const [settleOpen, setSettleOpen] = useState(false);
  const handleMarkSettled = () => {
    if (selected.size === 0) {
      toast.error(translate("company:payrolls.select_at_least_one_dispute"));
      return;
    }
    setSettleOpen(true);
  };

  const handleGenerate = async () => {
    if (selected.size === 0) {
      toast.error(translate("company:payrolls.select_at_least_one_dispute"));
      return;
    }
    const selectedItems = items.filter((i) => selected.has(i.id));
    const wpsItems = selectedItems.filter((i) => i.paymentMethod === "WPS");
    const nonWpsCount = selectedItems.length - wpsItems.length;
    if (wpsItems.length === 0) {
      toast.error(
        translate(
          "company:payrolls.selected_disputes_are_not_wps_payment_use_mark_as_settle",
        ),
      );
      return;
    }
    if (nonWpsCount > 0) {
      toast(
        `${nonWpsCount} non-WPS dispute(s) skipped. Use 'Mark as Settled' for those.`,
        { icon: "⚠️" },
      );
    }
    try {
      const res = await settle({
        disputeIds: wpsItems.map((i) => i.id),
      }).unwrap();
      const data = res.data;
      const filesText = data.paymentFiles
        .map((f) => `${f.fileName} (${f.currency} ${f.totalAmount.toFixed(2)})`)
        .join(", ");
      toast.success(
        `Settled ${data.settledCount} dispute(s). Files: ${filesText || "—"}`,
      );
      if (data.skipped.length > 0) {
        toast.error(
          `Skipped ${data.skipped.length}: ${data.skipped
            .map((s) => `#${s.disputeId} (${s.reason})`)
            .join(", ")}`,
          { duration: 6000 },
        );
      }
      setSelected(new Set());
      refetch();
    } catch (err: any) {
      const msg = err?.data?.message;
      toast.error(
        Array.isArray(msg)
          ? msg.join(", ")
          : (msg ?? translate("company:payrolls.failed_to_generate_wps")),
      );
    }
  };

  const columns: ColumnDef<ISettleableDispute>[] = [
    {
      key: "select",
      label: "",
      className: "w-[4%]",
      render: (r) => (
        <input
          type="checkbox"
          checked={selected.has(r.id)}
          onChange={() => toggle(r.id)}
          className="size-4 accent-primary cursor-pointer"
        />
      ),
    },
    {
      key: "employee",
      label: translate("common:labels.employee"),
      className: "w-[20%]",
      render: (r) => (
        <div className="flex items-center gap-2">
          <Avatar className="size-7">
            {r.raisedByUser?.profileImage && (
              <AvatarImage
                src={r.raisedByUser.profileImage}
                alt={r.raisedByUser.fullName}
              />
            )}
            <AvatarFallback className="bg-primary/10 text-primary text-[10px] font-bold">
              {getInitials(r.raisedByUser?.fullName ?? "?")}
            </AvatarFallback>
          </Avatar>
          <span className="text-sm font-medium text-gray-900 truncate">
            {r.raisedByUser?.fullName ?? "—"}
          </span>
        </div>
      ),
    },
    {
      key: "period",
      label: translate("company:payrolls.period"),
      className: "w-[10%]",
      render: (r) => (
        <span className="text-sm text-gray-700">
          {r.payrollRun?.periodStart
            ? format(new Date(r.payrollRun.periodStart), "MMM yyyy")
            : "—"}
        </span>
      ),
    },
    {
      key: "paymentMethod",
      label: translate("company:payrolls.payment_method"),
      className: "w-[16%]",
      render: (r) => {
        const isWps = r.paymentMethod === "WPS";
        const label = r.paymentMethod
          ? (PAYMENT_METHOD_KEY[r.paymentMethod] &&
              translate(PAYMENT_METHOD_KEY[r.paymentMethod])) ||
            r.paymentMethod
          : "—";
        return <Badge variant={isWps ? "success" : "neutral"}>{label}</Badge>;
      },
    },
    {
      key: "amount",
      label: translate("company:payrolls.adjustment"),
      className: "w-[14%]",
      render: (r) => (
        <span
          className={cn(
            "text-sm font-semibold tabular-nums",
            r.eligibleForWps ? "text-success" : "text-muted-foreground",
          )}
        >
          {r.currencyCode ?? "AED"} {formatAmount(r.adjustmentAmount)}
        </span>
      ),
    },
    {
      key: "resolvedAt",
      label: translate("company:payrolls.resolved"),
      className: "w-[14%]",
      render: (r) => (
        <span className="text-xs text-gray-500">
          {r.resolvedAt ? format(new Date(r.resolvedAt), "dd MMM yyyy") : "—"}
        </span>
      ),
    },
    {
      key: "action",
      label: translate("company:payrolls.actions"),
      className: "w-[10%]",
      render: (r) => (
        <ViewAction
          tooltip={translate("company:payrolls.view")}
          onClick={() =>
            navigate(ROUTES.HOME.COMPANY.PAYROLL.DISPUTE_DETAIL(r.id))
          }
        />
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 text-xs text-gray-600">
          <button
            type="button"
            onClick={toggleAll}
            disabled={allCount === 0}
            className="px-3 py-1.5 border border-gray-200 rounded-xs hover:bg-gray-50 disabled:opacity-50"
          >
            {selectedCount === allCount && allCount > 0
              ? translate("company:payrolls.deselect_all")
              : translate("common:buttons.select_all")}
          </button>
          <span>
            <Trans
              t={translate}
              i18nKey="company:payrolls.selected_with_total"
              values={{
                count: selectedCount,
                amount: formatAmount(totalSelectedAmount),
              }}
              components={{
                count: <strong className="text-gray-900" />,
                amount: <strong className="text-success tabular-nums" />,
              }}
            />
          </span>
        </div>
        <div className="flex items-center gap-2">
          <PermissionButton
            section={PLATFORM_SECTION_ENUM.PAYROLL}
            action="edit"
            mode="hide"
            variant="outline"
            size="sm"
            onClick={handleMarkSettled}
            disabled={selected.size === 0}
          >
            {translate("company:payrolls.mark_as_settled")}
          </PermissionButton>
          <MarkSettledDialog
            open={settleOpen}
            disputes={items.filter((i) => selected.has(i.id))}
            onClose={() => setSettleOpen(false)}
            onDone={() => {
              setSettleOpen(false);
              setSelected(new Set());
              refetch();
            }}
          />
          <PermissionButton
            section={PLATFORM_SECTION_ENUM.PAYROLL}
            action="edit"
            mode="hide"
            variant="primary"
            size="sm"
            onClick={handleGenerate}
            disabled={isSettling || selected.size === 0 || !hasPayableSelected}
            title={
              selected.size > 0 && !hasWpsSelected
                ? translate(
                    "company:payrolls.none_of_selected_disputes_use_wps_payment",
                  )
                : selected.size > 0 && !hasPayableSelected
                  ? translate("company:payrolls.no_positive_wps_adjustment")
                  : undefined
            }
          >
            {isSettling
              ? translate("common:states.generating")
              : translate("company:payrolls.generate_wps")}
          </PermissionButton>
        </div>
      </div>

      <div className="rounded-xs border border-border bg-white">
        <Table<ISettleableDispute>
          columns={columns}
          data={items}
          loading={isFetching}
          emptyTitle={translate("company:payrolls.no_pending_settlements")}
          emptyMessage={translate(
            "company:payrolls.resolved_disputes_awaiting_wps_payment_will_appear_here",
          )}
        />
      </div>
    </div>
  );
};
